import { Injectable } from "@angular/core";
import { StateService } from "@core-services/state.service";
import { Observable } from "rxjs";
import { StakingState } from "@modules/staking/domain/entities/staking-state.model";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { TradeAmountType } from "@modules/shared/domain/entities/trade-amount-type.model";
import { StakingProcess } from "@modules/staking/domain/entities/staking-process.model";
import { StakeableAvailability } from "../entities/stakable-availability.model";
import { StakingFlowStep } from "@modules/shared/domain/entities/trade-flow.model";
import { DEFAULT_AMOUNT_OPTION } from "../entities/constants";

export const initialState: StakingState = {
	stakeables: [],
	isStakingEnabled: null,
	stakingProcess: {
		showPreScreen: false,
		currentStep: StakingFlowStep.WELCOME,
		selectedPercentage: DEFAULT_AMOUNT_OPTION,
		stakeable: null,
		wallet: null,
		amount: "0",
		amountType: TradeAmountType.ASSET,
		error: false,
		termsAccepted: false,
		type: null,
		result: null,
		isLoading: false,
		seenEarnTerms: false
	}
};

@Injectable({
	providedIn: "root"
})
export class StakingStateService extends StateService<StakingState> {
	constructor() {
		super(initialState);
	}

	public isStakingEnabled$(): Observable<boolean> {
		return this.select((state: StakingState) => state.isStakingEnabled);
	}

	public getStakeables$(): Observable<Stakeable[]> {
		return this.select((state: StakingState) => state.stakeables);
	}

	public getAvailableStakeables$(): Observable<Stakeable[]> {
		return this.select((state: StakingState) =>
			state.stakeables.filter((stakeable: Stakeable) => stakeable.availability !== StakeableAvailability.DISABLED)
		);
	}

	public getStakedAssets$(): Observable<Stakeable[]> {
		return this.select((state: StakingState) =>
			state.stakeables.filter((stakeable: Stakeable) => +stakeable.stakedAmount > 0)
		);
	}

	public getErrorState$(): Observable<boolean> {
		return this.select((state: StakingState) => state.stakingProcess.error);
	}

	public getLoadingState$(): Observable<boolean> {
		return this.select((state: StakingState) => state.stakingProcess.isLoading);
	}

	public updateStakeables(stakeables: Stakeable[]): void {
		this.setState({ ...this.state, stakeables });
	}

	public getStakingProcess$(): Observable<StakingProcess> {
		return this.select((state: StakingState) => state.stakingProcess);
	}

	public getStakingState$(): Observable<StakingState> {
		return this.select((state: StakingState) => state);
	}

	public goToStep(step: StakingFlowStep): void {
		this.setState({ ...this.state, stakingProcess: { ...this.state.stakingProcess, currentStep: step } });
	}

	public updateStakingProcess(process: Partial<StakingProcess>): void {
		this.setState({ ...this.state, stakingProcess: { ...this.state.stakingProcess, ...process } });
	}

	public resetStakingProcess(): void {
		const showPreScreen: boolean = this.state.stakingProcess.showPreScreen;
		this.setState({ ...this.state, stakingProcess: { ...initialState.stakingProcess, showPreScreen } });
	}

	public resetStakingProcessAmount(): void {
		this.setState({
			...this.state,
			stakingProcess: { ...this.state.stakingProcess, amount: "", amountType: TradeAmountType.ASSET }
		});
	}

	public updateStakingState(state: Partial<StakingState>): void {
		this.setState({ ...this.state, ...state });
	}
}
